import React, { useState, useEffect, useRef } from "react";
import Navbar from "../Navbar";
import { NavLink } from "react-router-dom";
import Footer from "../Footer";
import Carousel from "react-grid-carousel";
import { SEO } from "../SEO";
import HeroBG from "../../img/about-bg.avif";

function About() {
  const [twoisHovered, setTwoIsHovered] = useState(false);

  // Animation refs
  const heroRef = useRef(null);
  const missionVisionRef = useRef(null);
  const whatWeDoRef = useRef(null);
  const whyChooseRef = useRef(null);
  const teamRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          // Hero animation
          if (entry.target === heroRef.current) {
            const elements = entry.target.querySelectorAll(".hero-item");
            elements.forEach((element: any, index: any) => {
              setTimeout(() => {
                element.classList.add("animate-fade-in-up");
              }, index * 100);
            });
          }

          // Mission & Vision animation
          if (entry.target === missionVisionRef.current) {
            const cards = entry.target.querySelectorAll(".mission-card");
            cards.forEach((card: any, index: any) => {
              setTimeout(() => {
                card.classList.add("animate-scale-in");
              }, index * 200);
            });
          }

          // What We Do animation
          if (entry.target === whatWeDoRef.current) {
            const title = entry.target.querySelector(".section-title");
            if (title) {
              title.classList.add("animate-fade-in-up");
            }

            const items = entry.target.querySelectorAll(".what-item");
            items.forEach((item: any, index: any) => {
              setTimeout(
                () => {
                  item.classList.add("animate-fade-in-up");
                },
                (index + 1) * 150,
              );
            });

            const image = entry.target.querySelector(".what-image");
            if (image) {
              setTimeout(() => {
                image.classList.add("animate-slide-in-right");
              }, 300);
            }
          }

          // Why Choose Us animation
          if (entry.target === whyChooseRef.current) {
            const title = entry.target.querySelector(".section-title");
            if (title) {
              title.classList.add("animate-fade-in-up");
            }

            const cards = entry.target.querySelectorAll(".why-card");
            cards.forEach((card: any, index: any) => {
              setTimeout(
                () => {
                  card.classList.add("animate-scale-in");
                },
                (index + 1) * 150,
              );
            });
          }

          // Team animation
          if (entry.target === teamRef.current) {
            const title = entry.target.querySelector(".section-title");
            if (title) {
              title.classList.add("animate-fade-in-up");
            }

            const teamCards = entry.target.querySelectorAll(".team-card");
            teamCards.forEach((card: any, index: any) => {
              setTimeout(
                () => {
                  card.classList.add("animate-scale-in");
                },
                (index + 1) * 200,
              );
            });
          }

          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (heroRef.current) observer.observe(heroRef.current);
    if (missionVisionRef.current) observer.observe(missionVisionRef.current);
    if (whatWeDoRef.current) observer.observe(whatWeDoRef.current);
    if (whyChooseRef.current) observer.observe(whyChooseRef.current);
    if (teamRef.current) observer.observe(teamRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .initial-hidden {
  opacity: 0;
  transform: translateY(20px);
}

/* Fade in up animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 1;
  transform: translateY(0);
}

/* Scale in animation */
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scale-in {
  animation: scaleIn 0.5s ease-out forwards;
  opacity: 1;
  transform: scale(1);
}

/* Slide in from left */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in-left {
  animation: slideInLeft 0.6s ease-out forwards;
  opacity: 1;
  transform: translateX(0);
}

/* Slide in from right */
@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in-right {
  animation: slideInRight 0.6s ease-out forwards;
  opacity: 1;
  transform: translateX(0);
}
      `}</style>

      <SEO
        title="About us | Afripay"
        description="Get to know about Afripay on Afripay."
        canonical="https://goafripay.com/about-us"
      />

      <Navbar />
      <div className="bg-gradient-to-b from-[#FAFBFC] to-[#F8FFFE]">
        <div className="md:pt-[140px] pt-[120px]" ref={heroRef}>
          <div className="flex flex-col lg:flex-row justify-center items-center px-8">
            <div className="flex flex-col gap-2">
              <div className="hidden lg:block hero-item initial-hidden">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                  About <span className="text-[#11723D]">AfriPay</span>
                </h1>
              </div>
              <div className="lg:hidden block hero-item initial-hidden">
                <h1 className="text-3xl sm:text-4xl font-display font-bold text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                  About <span className="text-[#11723D]">AfriPay</span>
                </h1>
              </div>

              <p className="hero-item initial-hidden max-w-xl mb-6 mt-6 font-body font-medium text-base sm:text-lg text-[#525252] leading-relaxed">
                AFRIPAY is a leading fintech company devoted to revolutionizing
                the educational payment landscape for African students. With a
                focus on providing cutting-edge financial solutions, we aim to
                remove the barriers to accessing global education, making it
                more affordable and accessible.
              </p>

              <div className="flex flex-col md:flex-row items-center lg:items-start gap-5">
                <div className="flex flex-col gap-2 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] py-2 px-4 w-60 rounded-md">
                  <img
                    src="/images/target.png"
                    alt="Target"
                    width={80}
                    height={80}
                  />
                  <h2 className="font-bold text-xl">Our Mission</h2>
                  <p className="text-base text-[#525252] min-h-[170px]">
                    Empowering African students by providing seamless,
                    efficient, and secure cross-border payment solutions for
                    education.
                  </p>
                </div>
                <div className="flex flex-col gap-2 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] py-2 px-4 w-60 rounded-md">
                  <img
                    src="/images/eye.png"
                    alt="Target"
                    width={80}
                    height={80}
                  />
                  <h2 className="font-bold text-xl">Our Vision</h2>
                  <p className="text-base text-[#525252] min-h-[170px]">
                    To empower African students by breaking down financial
                    barriers, enabling them to achieve their educational dreams
                    and become global leaders.
                  </p>
                </div>
              </div>
            </div>

            <div className="hero-item initial-hidden flex lg:mt-[80px] mt-[40px]">
              <img
                src="/images/about-hero.avif"
                alt="About Hero"
                width={820}
                className="mb-8"
              />
            </div>
          </div>
        </div>

        {/* <section className="bg-gradient-to-b from-[#FAFBFC] to-[#F8FFFE] py-16" ref={missionVisionRef}>
          <div className="max-w-7xl mx-auto px-8 lg:px-16">
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-8 lg:gap-12">
              <div className="mission-card initial-hidden group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#0F5132]/20 hover:-translate-y-2">
                  <div className="flex flex-col items-center">
                    <img 
                      src="/images/mission1.svg" 
                      alt="Our Mission" 
                      className="w-full h-auto transition-transform duration-300 group-hover:scale-105" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="mission-card initial-hidden group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#0F5132]/20 hover:-translate-y-2">
                  <div className="flex flex-col items-center">
                    <img 
                      src="/images/vision.svg" 
                      alt="Our Vision" 
                      className="w-full h-auto transition-transform duration-300 group-hover:scale-105" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* <-- What We Do --> */}
        <section className="pt-10 md:pt-20 pb-10 " ref={whatWeDoRef}>
          <div className="flex md:flex-row flex-col px-8 lg:px-16">
            <div className="flex-1 flex justify-center items-start flex-col">
              <div className="md:flex flex-col w-full">
                <div className="flex justify-between">
                  <h1 className="section-title initial-hidden hidden lg:block flex-1 text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                    What We Do
                  </h1>
                </div>

                <h1 className=" lg:hidden block flex-1 text-2xl sm:text-3xl font-display font-bold text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                  What We Do
                </h1>
              </div>

              <div className="lg:mt-[44px] mt-[20px]">
                <div className="what-item initial-hidden flex items-center space-x-4">
                  <img
                    src="/images/fast.png"
                    alt="Fast"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Fast Tuition Payments
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Facilitating quick and hassle-free tuition fee
                      transactions, both online and offline.
                    </p>
                  </div>
                </div>
                <div className="what-item initial-hidden flex items-center space-x-4 mt-[20px]">
                  <img
                    src="/images/currency.png"
                    alt="Currency"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Multi-Currency Support
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Enabling payments in various currencies, with seamless
                      conversion options.
                    </p>
                  </div>
                </div>
                <div className="what-item initial-hidden flex space-x-4 mt-[20px]">
                  <img
                    src="/images/wallet.png"
                    alt="Wallet"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Diverse Payment Methods
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Supporting multiple payment options, including bank
                      transfers, cards, mobile money, and cryptocurrencies.
                    </p>
                  </div>
                </div>
                <div className="what-item initial-hidden flex items-center space-x-4 mt-[20px]">
                  <img
                    src="/images/scholar.png"
                    alt="Scholar"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Comprehensive Student Support
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Providing additional services such as affordable travel
                      arrangements, travel and health insurance, and more.
                    </p>
                  </div>
                </div>
                <div className="what-item initial-hidden flex items-center space-x-4 mt-[20px]">
                  <img
                    src="/images/world_about.png"
                    alt="World"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Interest-Earning Accounts
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Offering savings in international currencies with
                      attractive annual interest rates.
                    </p>
                  </div>
                </div>
                <div className="what-item initial-hidden flex items-center space-x-4 mt-[20px]">
                  <img
                    src="/images/card_about.png"
                    alt="Card"
                    className="w-[60px] h-[60px] md:w-15 md:h-15"
                  />
                  <div>
                    <h3 className="font-display font-semibold text-[#0a0a0a] text-sm md:text-lg">
                      Virtual and Physical Cards
                    </h3>
                    <p className="text-[#525252] text-xs md:text-base font-body font-medium leading-relaxed">
                      Simplifying transactions with easy-to-use cards for
                      students.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="what-image initial-hidden flex-1 flex-col justify-center items-center md:mt-0 mt-[50px]">
              {/* <img src="/images/img1.png" alt="hero" /> */}
              <p>
                At AFRIPAY, we specialize in offering comprehensive payment
                solutions tailored to the unique needs of African students
                pursuing education abroad.
              </p>

              <div className="lg:block hidden mt-8">
                <img
                  src={
                    twoisHovered ? "/images/img1 anime.gif" : "/images/img1.png"
                  }
                  alt="Image"
                  loading="lazy"
                  onMouseOver={() => setTwoIsHovered(true)}
                  onMouseOut={() => setTwoIsHovered(false)}
                  className="w-full h-auto"
                />
              </div>
              <div className="block lg:hidden">
                <img
                  src={"/images/img1 anime.gif"}
                  alt="Image"
                  onMouseOver={() => setTwoIsHovered(true)}
                  onMouseOut={() => setTwoIsHovered(false)}
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* <-- Why Choose Us --> */}
        <section className="py-10 md:py-20 " ref={whyChooseRef}>
          <div className="container mx-auto px-8 lg:px-16">
            <div className="mb-12">
              <h1 className="section-title initial-hidden text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-center text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                Why Choose AFRIPAY
              </h1>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 justify-items-center items-center gap-10 lg:gap-0">
              <div className="bg-[#ecf2ee] flex flex-col items-center w-56 px-2.5 pb-3 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] rounded-md text-center">
                <img src="/images/person.png" alt="Person" width={170} />
                <h2 className="text-[#11723D] text-base sm:text-lg font-semibold">
                  Customer-Centric Approach
                </h2>
                <p className="text-[#525252] text-sm">
                  Our dedicated support team is committed to assisting students
                  to at every step of their educational journey
                </p>
              </div>
              <div className="bg-[#eaf6f2] flex flex-col items-center w-56 px-2.5 pb-3 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] rounded-md text-center">
                <img src="/images/trophy.png" alt="Person" width={213} />
                <h2 className="text-[#11723D] text-base sm:text-lg font-semibold">
                  Proven Success
                </h2>
                <p className="text-[#525252] text-sm">
                  With over 350 students successfully studying in various parts
                  of the world, we boast a high successt rate and strong track
                  record
                </p>
              </div>
              <div className="bg-[#fff4e0] flex flex-col items-center w-56 px-2.5 pb-3 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] rounded-md text-center">
                <img src="/images/phone_about.png" alt="Person" width={142} />
                <h2 className="text-[#11723D] text-base sm:text-lg font-semibold">
                  Advanced Technology
                </h2>
                <p className="text-[#525252] text-sm">
                  We leverage cutting-edge technology to provide secure,
                  efficient, and reliable financial services.
                </p>
              </div>
              <div className="bg-[#e2eefa] flex flex-col items-center w-56 px-2.5 pb-3 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] rounded-md text-center">
                <img src="/images/safe.png" alt="Person" width={180} />
                <h2 className="text-[#11723D] text-base sm:text-lg font-semibold">
                  Tailored Solutions
                </h2>
                <p className="text-[#525252] text-sm">
                  Our platform is designed specifically for the needs of African
                  students ensuring a personalized and supportive experience
                </p>
              </div>
              <div className="bg-[#e2eefa] flex flex-col items-center w-56 px-2.5 pb-3 shadow-[0px_4px_6px_-2px_#10182808,0px_12px_16px_-4px_#10182814] rounded-md text-center">
                <img src="/images/secure.png" alt="Person" width={180} />
                <h2 className="text-[#11723D] text-base sm:text-lg font-semibold">
                  Secure & Reliable
                </h2>
                <p className="text-[#525252] text-sm">
                  Your transactions and personal information are protected with
                  top-tier security measures
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Meet The Team */}
        {/* <section className="pt-10">
          <div className="  px-8 lg:px-16 pb-8  lg:gap-8 xl:gap-0 lg:pt-8">
            <div className="">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-center text-[#0a0a0a] leading-tight tracking-[-0.02em]">
                Meet The Team
              </h1>
            </div>
            <div className='grid lg:grid-cols-4 grid-cols-1 gap-8 pt-6'>
              <div className="flex flex-col items-center">
                <div className="flex justify-center">
                  <img
                    src="/images/john.png"
                    alt="hero"

                  />
                </div>
                <div className="flex justify-center mt-2.5">
                  <div>
                    <h3 className="text-center text-base sm:text-lg lg:text-xl font-display font-semibold text-[#0a0a0a]">
                      John Samuel
                    </h3>
                    <div className="bg-[#26A869] text-center text-white py-1">CEO</div>
                    <p className="text-[#696969] lg:text-[12px] text-[12px] text-center mt-2">
                      Experienced Founder proficient in IT services, with a strong track record in business development, marketing, and sales. Expert in connecting European and African markets.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex justify-center">
                  <img
                    src="/images/cfo.png"
                    alt="hero"

                  />
                </div>
                <div className="flex justify-center mt-3.5">
                  <div>
                    <h3 className="text-center text-base sm:text-lg lg:text-xl font-display font-semibold text-[#0a0a0a]">
                      Desmond Abollo
                    </h3>
                    <div className="bg-[#26A869] text-center text-white py-1">Chief Financial Officer</div>
                    <p className="text-[#696969] lg:text-[12px] text-[12px] text-center mt-2">
                      An experienced Chief Financial Officer with a proven track record in financial strategy and management.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex justify-center">
                  <img
                    src="/images/PM.png"
                    alt="hero"

                  />
                </div>
                <div className="flex justify-center mt-4">
                  <div>
                    <h3 className="text-center text-base sm:text-lg lg:text-xl font-display font-semibold text-[#0a0a0a]">
                      John Odoemenem
                    </h3>
                    <div className="bg-[#26A869] text-center text-white py-1">Product Lead</div>
                    <p className="text-[#696969] lg:text-[12px] text-[12px] text-center mt-2">
                      Leads product strategy and execution, aligning user needs with business goals. Ensures successful product delivery.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex justify-center">
                  <img
                    src="/images/Rose.png"
                    alt="hero"
                  />
                </div>
                <div className="flex justify-center mt-2">
                  <div>
                    <h3 className="text-center text-base sm:text-lg lg:text-xl font-display font-semibold text-[#0a0a0a]">
                      Roseline Ibeosu
                    </h3>
                    <div className="bg-[#26A869] text-center text-white py-1">Customer Success Lead</div>
                    <p className="text-[#696969] lg:text-[12px] text-[12px] text-center mt-2">
                      Champions customer satisfaction and retention by driving success strategies and nurturing client relationships.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section> */}

        {/* Meet The Team */}
        <section className="" ref={teamRef}>
          <div
            style={{
              backgroundImage: `url(${HeroBG})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
            }}
            className="max-w-7xl mx-auto rounded-lg relative bg-[#11723D] py-6 px-10 flex flex-col md:flex-row items-center justify-between text-white mb-6"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <img src="/images/logo_white.png" alt="Logo Alt" width={120} />
              <p className="md:w-[260px]">
                The modern way to send, spend, and manage your money globally.
              </p>
            </div>
            <div className="w-full grid grid-cols-2 md:grid-cols-4 mt-10 md:mt-6 gap-y-8 md:my-0 md:gap-y-0">
              <div className="flex items-center gap-1.5 px-3 md:border-r border-white">
                <img src="/images/secure-2.png" alt="Secure" width={50} />
                <h4>
                  Secure & <br /> Trusted
                </h4>
              </div>
              <div className="flex items-center gap-1.5 px-3 md:border-r border-white">
                <img src="/images/fast-2.png" alt="Fast" width={50} />
                <h4>
                  Fast & <br /> Reliable
                </h4>
              </div>
              <div className="flex items-center gap-1.5 px-3 md:border-r border-white">
                <img src="/images/world-2.png" alt="Fast" width={50} />
                <h4>
                  Global <br />
                  Access
                </h4>
              </div>
              <div className="flex items-center gap-1.5 px-3">
                <img src="/images/phone-2.png" alt="Fast" width={50} />
                <h4>
                  Dedicated <br />
                  Support
                </h4>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* <-- Footer --> */}
      <Footer />
    </>
  );
}

export default About;
